import { useState } from "react";
import { signIn } from "aws-amplify/auth";
import { TextInput, PasswordInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { z } from "zod";
import { usePostHog } from "posthog-js/react";
import { AuthEvent } from "../../../data/auth-events";
import { emailSchema, passwordRequiredSchema } from "./validation-schemas";
import styles from "./claimable-authenticator-v2.module.scss";

const signInFormSchema = z.object({
  email: emailSchema,
  password: passwordRequiredSchema,
});

type SignInFormValues = z.infer<typeof signInFormSchema>;

type SignInFormProps = {
  onSignInSuccess: () => void;
  onSwitchToSignUp?: () => void;
  onForgotPassword: () => void;
  onRequireConfirmation?: (email: string) => void;
  prefilledEmail?: string;
  showAccountVerifiedMessage?: boolean;
  showPasswordResetMessage?: boolean;
};

const SignInForm = ({
  onSignInSuccess,
  onSwitchToSignUp,
  onForgotPassword,
  onRequireConfirmation,
  prefilledEmail,
  showAccountVerifiedMessage,
  showPasswordResetMessage,
}: SignInFormProps) => {
  const posthog = usePostHog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<SignInFormValues>({
    initialValues: {
      email: prefilledEmail || "",
      password: "",
    },
    validate: zod4Resolver(signInFormSchema),
  });

  const handleSubmit = async (values: SignInFormValues) => {
    posthog?.capture(AuthEvent.SignInSubmit);
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await signIn({
        username: values.email,
        password: values.password,
      });

      if (result.isSignedIn) {
        posthog?.capture(AuthEvent.SignInSuccess);
        onSignInSuccess();
      } else if (result.nextStep) {
        const { signInStep } = result.nextStep;
        posthog?.capture(AuthEvent.SignInChallenge, { step: signInStep });

        if (signInStep === "CONFIRM_SIGN_UP" && onRequireConfirmation) {
          onRequireConfirmation(values.email);
        } else if (
          signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          setError(
            "You need to set a new password. Please use the forgot password flow.",
          );
        } else {
          setError(
            `Additional verification required: ${signInStep}. Please contact support.`,
          );
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred during sign in";
      posthog?.capture(AuthEvent.SignInError, { error: errorMessage });
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    posthog?.capture(AuthEvent.ForgotPasswordClick);
    onForgotPassword();
  };

  return (
    <div className={styles.signInForm}>
      <img
        src="/images/logo/logo_black_large.svg"
        alt="Claimable"
        className={styles.formLogo}
      />
      <h1 className={styles.formTitle}>{"Welcome back"}</h1>
      <p className={styles.formSubtitle}>{"Log in to continue your appeal"}</p>

      {showAccountVerifiedMessage && (
        <div className={styles.accountVerifiedBanner}>
          {"Your account has been verified! Please sign in to continue."}
        </div>
      )}

      {showPasswordResetMessage && (
        <div className={styles.accountVerifiedBanner}>
          {
            "Your password has been reset! Please sign in with your new password."
          }
        </div>
      )}

      <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form}>
        <TextInput
          label="Email"
          placeholder="you@example.com"
          size="lg"
          value={form.values.email}
          onChange={(e) => form.setFieldValue("email", e.currentTarget.value)}
          onBlur={() => form.validateField("email")}
          error={form.errors.email}
          disabled={Boolean(prefilledEmail)}
          classNames={{
            input: styles.mantineInput,
            label: styles.inputLabel,
          }}
        />

        <div className={styles.passwordFieldWrapper}>
          <PasswordInput
            label="Password"
            placeholder="Enter your password"
            size="lg"
            value={form.values.password}
            onChange={(e) =>
              form.setFieldValue("password", e.currentTarget.value)
            }
            onBlur={() => form.validateField("password")}
            error={form.errors.password}
            classNames={{
              input: styles.mantineInput,
              label: styles.inputLabel,
            }}
          />
          <button
            type="button"
            onClick={handleForgotPassword}
            className={styles.forgotPasswordLink}
          >
            {"Forgot password?"}
          </button>
        </div>

        {error && <p className={styles.formError}>{error}</p>}

        <button
          type="submit"
          className={styles.primaryButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Logging in..." : "LOG IN"}
        </button>
      </form>

      {onSwitchToSignUp && (
        <div className={styles.switchAuthMode}>
          <span>{"Don't have an account? "}</span>
          <button
            type="button"
            onClick={onSwitchToSignUp}
            className={styles.switchLink}
          >
            {"Sign up"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SignInForm;
